import {
  CertificatesContexts,
  getVesselCertificates,
  type VesselTypeCode,
  type VRCertificateType,
} from "@constants/certificates";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import { CertificateFields, FORM_FIELDS } from "@features/vessel-register/utils";
import type {
  AdditionalDocument,
  BunkerBargeCertificate,
  BunkerBargeCertificateType,
  BunkerBargeDocument,
  Vessel,
} from "@services/vessel/types";
import { normalizeDate, today } from "@utils/date-time";

type BunkerBargeCertificateFormFieldConfig = {
  certificateType: BunkerBargeCertificateType;
  documentField: keyof VesselRegisterFormData;
  issueDateField?: keyof VesselRegisterFormData;
  expiryDateField?: keyof VesselRegisterFormData;
};

const BUNKER_BARGE_CERTIFICATE_FORM_FIELD_CONFIG: BunkerBargeCertificateFormFieldConfig[] = [
  {
    certificateType: "NAVIGATION_LICENSE",
    documentField: FORM_FIELDS.NAVIGATION_LICENSE,
    issueDateField: FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE,
    expiryDateField: FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
  },
  {
    certificateType: "CHARTER_PARTY_AGREEMENT",
    documentField: FORM_FIELDS.CHARTER_PARTY_AGREEMENT,
    issueDateField: FORM_FIELDS.CHARTER_START_DATE,
    expiryDateField: FORM_FIELDS.CHARTER_END_DATE,
  },
  {
    certificateType: "OIL_SPILL_COMPANY",
    documentField: FORM_FIELDS.OIL_SPILL_COMPANY,
    issueDateField: FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE,
    expiryDateField: FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
  },
  {
    certificateType: "SIRE_REPORT",
    documentField: FORM_FIELDS.SIRE_REPORT,
    issueDateField: FORM_FIELDS.SIRE_REPORT_FROM_DATE,
    expiryDateField: FORM_FIELDS.SIRE_REPORT_TO_DATE,
  },
  {
    certificateType: "HOSE_TEST_CERTIFICATE",
    documentField: FORM_FIELDS.HOSE_TEST_CERTIFICATE,
    expiryDateField: FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE,
  },
  {
    certificateType: "SHIP_TO_SHIP_PLAN",
    documentField: FORM_FIELDS.SHIP_TO_SHIP_PLAN,
    issueDateField: FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE,
    expiryDateField: FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE,
  },
];

const mapCertificates = (vessel: Vessel) => {
  const permanentCertificateTypes = new Set(
    getVesselCertificates(
      CertificatesContexts.BASE,
      vessel.vesselType?.vesselTypeCode as VesselTypeCode,
      vessel.grt,
    )
      .filter((cert) => cert.permanent === true)
      .map((cert) => cert.type),
  );

  return (vessel.vesselCertificates ?? []).reduce<
    NonNullable<VesselRegisterFormData[typeof FORM_FIELDS.CERTIFICATES]>
  >((acc, cert) => {
    const type = cert?.certificateType as VRCertificateType;
    return {
      ...acc,
      [type]: {
        [CertificateFields.URL]: cert?.certificateUrl ?? "",
        [CertificateFields.DOCUMENT_NAME]: cert?.certificateDocumentName ?? "",
        [CertificateFields.CLASS_NAME]: cert?.certificateClassName ?? "",
        [CertificateFields.EXPIRY_DATE]: cert?.certificateExpiryDate
          ? new Date(cert?.certificateExpiryDate)
          : null,
        [CertificateFields.PERMANENT]:
          permanentCertificateTypes.has(type) &&
          (vessel.markedAsDraft ? cert?.permanent === true : !cert?.certificateExpiryDate),
      },
    };
  }, {});
};

const mapAdditionalDocuments = (additionalDocuments: AdditionalDocument[]) => {
  return additionalDocuments.map((doc) => ({
    documentType: doc.documentType ?? "DOCUMENT",
    documentUrl: doc.documentUrl,
    fileName: doc.fileName,
    otherSpecifyType: doc.otherSpecifyType ?? "",
    documentName: (doc.documentName ?? doc.fileName).slice(0, 50),
    documentSummary: doc.documentSummary ?? "",
    issueDate: doc.documentIssueDate ? new Date(doc.documentIssueDate) : null,
    expiryDate: doc.documentExpiryDate ? new Date(doc.documentExpiryDate) : null,
  }));
};

const mapBunkerBargeCertificateEntries = (certificates: BunkerBargeCertificate[] | undefined) =>
  (certificates ?? [])
    .filter((certificate) => !!certificate.documentUrl)
    .map((certificate) => ({
      name: certificate.name ?? "",
      url: certificate.documentUrl ?? "",
    }));

const mapBunkerBargeAdditionalDocumentEntries = (documents: BunkerBargeDocument[] | undefined) =>
  (documents ?? [])
    .filter((document) => !!document.documentUrl)
    .map((document) => ({
      name: document.fileName ?? "",
      url: document.documentUrl ?? "",
      type: document.documentType,
      documentType: document.documentType,
      otherSpecifyType: document.otherSpecifyType ?? "",
      documentName: (document.documentName ?? document.fileName).slice(0, 50),
      documentSummary: document.documentSummary ?? "",
      issueDate: document.documentIssueDate ?? null,
      expiryDate: document.documentExpiryDate ?? null,
    }));

const groupBunkerBargeCertificatesByType = (
  certificates: BunkerBargeCertificate[] | undefined,
): Partial<Record<BunkerBargeCertificateType, BunkerBargeCertificate[]>> =>
  (certificates ?? []).reduce<
    Partial<Record<BunkerBargeCertificateType, BunkerBargeCertificate[]>>
  >((acc, certificate) => {
    acc[certificate.certificateType] = [...(acc[certificate.certificateType] ?? []), certificate];
    return acc;
  }, {});

const mapBunkerBargeCertificateFormValues = (
  certificates: BunkerBargeCertificate[] | undefined,
) => {
  const certificatesByType = groupBunkerBargeCertificatesByType(certificates);

  return BUNKER_BARGE_CERTIFICATE_FORM_FIELD_CONFIG.reduce<Partial<VesselRegisterFormData>>(
    (acc, { certificateType, documentField, issueDateField, expiryDateField }) => {
      const certificatesForType = certificatesByType[certificateType];
      const certificate = certificatesForType?.[0];

      return {
        ...acc,
        [documentField]: mapBunkerBargeCertificateEntries(certificatesForType),
        ...(issueDateField ? { [issueDateField]: certificate?.issueDate ?? "" } : {}),
        ...(expiryDateField ? { [expiryDateField]: certificate?.expiryDate ?? "" } : {}),
      };
    },
    {},
  );
};

const mapVesselResponseToFormData = (vessel: Vessel): VesselRegisterFormData => {
  const effectiveEndDate = vessel.bunkerBargeDetails?.effectiveEndDate;
  const normalizedEndDate = effectiveEndDate ? normalizeDate(effectiveEndDate) : null;
  const shouldMapBunkerBarge = !normalizedEndDate || normalizedEndDate >= today();
  const bunkerBargeDetails = shouldMapBunkerBarge ? vessel.bunkerBargeDetails : undefined;
  const bunkerBargeFields: Partial<VesselRegisterFormData> = shouldMapBunkerBarge
    ? {
        ...mapBunkerBargeCertificateFormValues(bunkerBargeDetails?.certificates),
        [FORM_FIELDS.NATURE_OF_SERVICE]: bunkerBargeDetails?.natureOfServiceCode ?? "BUNKERING",
        [FORM_FIELDS.BUNKERING_COMPANY]: bunkerBargeDetails?.bunkeringCompany.companyCode ?? "",
        [FORM_FIELDS.REGISTERED_OWNER]: bunkerBargeDetails?.charterOwner ?? "",
        [FORM_FIELDS.OIL_COMPANY]: bunkerBargeDetails?.oilCompany.companyCode ?? "",
        [FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE]:
          bunkerBargeDetails?.companyNameHoseTestCertificate ?? "",
        [FORM_FIELDS.EFFECTIVE_START_DATE]: bunkerBargeDetails?.effectiveStartDate ?? "",
        [FORM_FIELDS.EFFECTIVE_END_DATE]: bunkerBargeDetails?.effectiveEndDate ?? "",
        [FORM_FIELDS.REMARKS]: bunkerBargeDetails?.remarks ?? "",
        [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS]: mapBunkerBargeAdditionalDocumentEntries(
          bunkerBargeDetails?.additionalDocuments,
        ),
      }
    : {};

  return {
    [FORM_FIELDS.VESSEL_NAME]: vessel.vesselName ?? "",
    [FORM_FIELDS.IMO]: vessel.vesselImoNumber,
    [FORM_FIELDS.OFFICIAL_NUMBER]: vessel.officialNumber ?? "",
    [FORM_FIELDS.NO_IMO_NUMBER]: vessel.markedAsDraft
      ? Boolean(vessel.noImo)
      : !vessel.vesselImoNumber,
    [FORM_FIELDS.CALL_SIGN]: vessel.vesselCallSign ?? "",
    [FORM_FIELDS.PORT_OF_REGISTRATION]: vessel.port?.portCode ?? "",
    [FORM_FIELDS.YEAR_BUILT]: vessel.shipBuildYear,
    [FORM_FIELDS.DATE_OF_DELIVERY]: vessel.dateOfDelivery ?? "",
    [FORM_FIELDS.FLAG]: vessel.country?.countryCode ?? "",
    [FORM_FIELDS.VESSEL_TYPE_CODE]: vessel.vesselType?.vesselTypeCode ?? "",
    [FORM_FIELDS.GROSS_TONNAGE]: vessel.grt,
    [FORM_FIELDS.NET_TONNAGE]: vessel.nrt,
    [FORM_FIELDS.DEADWEIGHT_TONNAGE]: vessel.dwt,
    [FORM_FIELDS.LENGTH_OVERALL]: vessel.loa,
    [FORM_FIELDS.DRAFT_MAX]: vessel.draftMax,
    [FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY]:
      vessel.vesselClassificationSociety?.vesselClassificationSocietyCode ?? "",
    [FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY]:
      vessel.otherVesselClassificationSociety ?? "",
    [FORM_FIELDS.BEAM]: vessel.beam,
    [FORM_FIELDS.SHIP_OWNER]: vessel.shipOwnership?.shipsOwner ?? "",
    [FORM_FIELDS.SHIP_CHARTERER]: vessel.shipOwnership?.shipsCharterer ?? "",
    [FORM_FIELDS.SHIPS_MANAGEMENT]: vessel.shipOwnership?.shipsManagement ?? "",
    [FORM_FIELDS.SHIPS_REGISTERED_OWNER]: vessel.shipOwnership?.shipsRegisteredOwner ?? "",
    [FORM_FIELDS.PI_CLUB]: vessel.shipOwnership?.piClub?.piClubCode ?? "",
    [FORM_FIELDS.MLC_CLUB]: vessel.shipOwnership?.mlcPiClub?.piClubCode ?? "",
    [FORM_FIELDS.PI_CLUB_OTHER]: vessel.shipOwnership?.piClub?.piClubName ?? "",
    [FORM_FIELDS.MLC_CLUB_OTHER]: vessel.shipOwnership?.mlcPiClub?.piClubName ?? "",
    [FORM_FIELDS.REGISTRATION_REMARKS]: vessel.remarks ?? "",
    ...bunkerBargeFields,
    [FORM_FIELDS.CERTIFICATES]: mapCertificates(vessel),

    [FORM_FIELDS.ADDITIONAL_DOCUMENTS]: mapAdditionalDocuments(vessel.additionalDocuments ?? []),
    [FORM_FIELDS.BUNKER_REGISTERED]: vessel.bunkerRegistered ?? false,
    [FORM_FIELDS.DIPLOMATIC_VESSEL]: vessel.diplomaticVessel ?? false,
  };
};

export default mapVesselResponseToFormData;
